import FormPage from '../../components/ui/FormPage.tsx';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { handlePtBrSubmit, ptBrValidityProps } from '../../utils/htmlValidation.ts';
import { assertNonNegative, assertMinNumber } from '../../utils/numberGuards.ts';

const FrotaNovoCusto: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    category: 'combustivel',
    description: '',
    amount: '',
    costDate: new Date().toISOString().split('T')[0],
    liters: '',
    pricePerLiter: '',
    postToCashFlow: false,
    notes: '',
  });

  const { data: vehicle } = useQuery(
    ['fleet-vehicle', id],
    async () => (await api.get(`/patrimonio/frota/${id}`)).data,
    { enabled: !!id }
  );

  const mutation = useMutation(
    async () => {
      const payload: any = {
        category: form.category,
        description: form.description,
        amount: Number(form.amount),
        costDate: form.costDate,
        liters: form.liters === '' ? undefined : Number(form.liters),
        pricePerLiter: form.pricePerLiter === '' ? undefined : Number(form.pricePerLiter),
        postToCashFlow: form.postToCashFlow,
        notes: form.notes || undefined,
      };
      return (await api.post(`/patrimonio/frota/${id}/custos`, payload)).data;
    },
    {
      onSuccess: () => navigate(`/patrimonio/frota/${id}/custos`),
      onError: (err: any) => setError(err?.response?.data?.error || 'Erro ao registrar custo'),
    }
  );

  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <FormPage
      backTo={`/patrimonio/frota/${id}/custos`}
      backLabel="Voltar"
      breadcrumb="Patrimônio / Frota / Novo custo"
      title="Novo custo"
      subtitle={`${vehicle?.make || ''} ${vehicle?.model || ''} · ${vehicle?.licensePlate || ''}`.trim()}
    >
{error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <form
        noValidate
        className="space-y-4"
        onSubmit={e =>
          handlePtBrSubmit(e, () => {
            const errs = [
              assertMinNumber(form.amount, 0.01, 'Valor'),
              assertNonNegative(form.liters, 'Litros'),
              assertNonNegative(form.pricePerLiter, 'Preço / L'),
            ].filter(Boolean);
            if (errs.length) {
              setError(errs[0] as string);
              return;
            }
            setError('');
            mutation.mutate();
          })
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Categoria *</span>
            <select
              value={form.category}
              onChange={e => set('category', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            >
              <option value="combustivel">Combustível</option>
              <option value="manutencao">Manutenção</option>
              <option value="pedagio">Pedágio</option>
              <option value="estacionamento">Estacionamento</option>
              <option value="lavagem">Lavagem</option>
              <option value="outros">Outros</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Data *</span>
            <input
              required
              {...ptBrValidityProps}
              type="date"
              value={form.costDate}
              onChange={e => set('costDate', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-gray-600 block mb-1">Descrição *</span>
            <input
              required
              {...ptBrValidityProps}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              placeholder="Gasolina · posto Shell…"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-600 block mb-1">Valor (R$) *</span>
            <input
              required
              type="number"
                            step="0.01"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </label>
          {form.category === 'combustivel' && (
            <>
              <label className="text-sm">
                <span className="text-gray-600 block mb-1">Litros</span>
                <input
                  type="number"
                                    step="0.01"
                  value={form.liters}
                  onChange={e => set('liters', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2"
                />
              </label>
              <label className="text-sm">
                <span className="text-gray-600 block mb-1">Preço / L (R$)</span>
                <input
                  type="number"
                                    step="0.01"
                  value={form.pricePerLiter}
                  onChange={e => set('pricePerLiter', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2"
                />
              </label>
            </>
          )}
          <label className="text-sm sm:col-span-2">
            <span className="text-gray-600 block mb-1">Observações</span>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </label>
        </div>

        <label className="flex items-start gap-3 text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <input
            type="checkbox"
            checked={form.postToCashFlow}
            onChange={e => set('postToCashFlow', e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <strong className="block text-gray-900">Lançar no Fluxo de caixa</strong>
            <span className="text-gray-500">
              Cria uma saída no financeiro (opção no formulário — não é CTA primário).
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(`/patrimonio/frota/${id}/custos`)}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isLoading}
            className="btn btn-primary"
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar custo'}
          </button>
        </div>
      </form>
    </FormPage>
  );
};

export default FrotaNovoCusto;
